import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import userAvatar from 'src/app/images/user-avatar.svg';
import moment from 'moment';
import { statusBadgeColor, statusBadgeText } from '../utils/BadgeEssay';
import { FiEdit3, FiEye, FiUpload } from 'react-icons/fi';
import { useClient } from 'jsonapi-react';
import { ModalWarning } from '@/components/custom/modal/modal-warning';
import usePermission from '@/app/hooks/usePermission';

export default function EssayStudentCard({ student, essay, currentUser }) {
  const navigate = useNavigate();
  const [openWarning, setOpenWarning] = useState(false);
  const statusClass = statusBadgeColor?.[student.status];
  const badgeText = statusBadgeText?.[student.status];
  const client = useClient();

  const canSend = usePermission('Enviar redação', 'Redação');

  const handleReviewEssay = () => {
    if (student.status === 'unreviewed') {
      client.mutate(['essays', essay.id, 'essay_students', student.id], { status: 'reviewing' });
    }
    navigate(`/redacao/correcao/${student.id}/${essay.id}`);
    setOpenWarning(false);
  };

  function renderAction() {
    const { status, id, corrector, created_by, user } = student;

    const viewEnabled =
      status === 'reviewed' || // corrigida pelo professor
      status === 'unreviewed' || // correção por IA
      status === 'annulled_by_teacher' || // anulada pelo professor
      status === 'reviewing' || // está sendo corrigida
      status === 'annulled'; // anulada

    const reviewEnabled =
      status === 'unreviewed' || // correção por IA
      (status === 'reviewing' && corrector.id === currentUser); // corrigindo e quem está corrigindo é o usuário atual

    const reachedSubmissionLimit = () => {
      const limit = essay['allow-multiple-submissions'] ? 2 : 1;
      return student['submission_order'] >= limit;
    };

    const isInvalid = () => ['annulled', 'annulled_by_teacher'].includes(student.status);

    const sendDisabled =
      !canSend || // não tem permissão
      (reachedSubmissionLimit() && status !== 'ocr_review' && status !== 'minimum_length_not_satisfied') || // já enviou a quantidade permitida e não está em revisão OCR e nem é inválida (mínimo de caracteres não atendido)
      status === 'reviewing' || // está sendo corrigida
      status === 'processing' || // está sendo processada
      (isInvalid() && reachedSubmissionLimit()) || // é inválida e já enviou a quantidade permitida
      status === 'close_for_submissions' || // prazo de envio encerrado
      essay.status === 'close_for_submissions' || // prazo de envio encerrado (status geral da redação)
      status === 'unreviewed'; // correção por IA

    return (
      <div className="flex gap-2">
        <button
          type="button"
          className="btn btn--outline"
          onClick={() => navigate(`/redacao/enviar/${essay.id}/${user?.id}`)}
          disabled={sendDisabled}
        >
          <FiUpload className="btn__icon" /> <span style={{ minWidth: '82px', fontSize: '12px' }}>Enviar redação</span>
        </button>
        <button
          type="button"
          className="btn btn--outline"
          onClick={() => navigate(`/redacao/visualizar/${id}/${essay.id}`)}
          disabled={!viewEnabled}
        >
          <FiEye className="btn__icon" /> <span style={{ fontSize: '12px' }}>Visualizar</span>
        </button>
        <button
          type="button"
          className="btn btn--outline"
          onClick={() => {
            if (status === 'reviewing' && corrector.id === currentUser) {
              navigate(`/redacao/correcao/${id}/${essay.id}`);
            } else {
              setOpenWarning(true);
            }
          }}
          disabled={!reviewEnabled}
        >
          <FiEdit3 className="btn__icon" /> <span style={{ fontSize: '12px' }}>Corrigir</span>
        </button>
      </div>
    );
  }

  return (
    <>
      <tr className="round-dg__row">
        <th className="round-dg__cell-header round-dg__cell-header--row">
          <div
            className="round-dg__user u-cursor-pointer"
            onClick={() => navigate(`/usuario/metricas/${student.user?.id}`)}
          >
            <img
              className="avatar avatar--xs"
              src={student.user?.image || userAvatar}
              alt="Imagem do usuário"
            />
            <div className="round-dg__user-inner">
              <h3
                className="round-dg__title"
                title={student.user?.name}
              >
                {student.user?.name}
              </h3>
              {/* <p className="round-dg__subtitle">email</p> */}
            </div>
          </div>
        </th>

        <td className="round-dg__cell">{student.status === 'ocr_review' ? student['submission_order'] - 1 : student['submission_order']}</td>
        <td className="round-dg__cell">{student['delivery-date'] ? moment(student['delivery-date']).format('DD/MM/YYYY') : '-'}</td>

        <td className="round-dg__cell">{student.grade}</td>

        <td className="round-dg__cell">
          <span
            title={badgeText}
            className={`badge badge--tiny ${statusClass}`}
          >
            {badgeText}
          </span>
        </td>

        <td className="round-dg__cell">{student.corrector?.name || '-'}</td>

        <td className="round-dg__cell">{renderAction()}</td>
      </tr>
      <ModalWarning
        show={openWarning}
        onClose={() => setOpenWarning(false)}
        titleFirstButton="Não"
        titleSecondButton="Sim"
        title={`Deseja mesmo corrigir essa redação?`}
        description="Outros professores não poderão corrigi-la."
        onSubmitFirstButton={() => setOpenWarning(false)}
        onSubmitSecondButton={() => handleReviewEssay()}
      />
    </>
  );
}
